import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  contentChild,
  signal,
} from '@angular/core';

/**
 * Marks a password `<input>` so the surrounding `<app-password-field>` can
 * flip it between hidden and visible. It owns the input's `type`, so don't
 * also set `type=` on the element.
 *
 * Autocorrect/autocapitalize are switched off because a phone that "fixes"
 * a password while it's shown in plain text defeats the point of showing it.
 */
@Directive({
  selector: 'input[appPasswordInput]',
  host: {
    '[attr.type]': 'visible() ? "text" : "password"',
    autocapitalize: 'off',
    autocorrect: 'off',
    spellcheck: 'false',
  },
})
export class PasswordInput {
  readonly visible = signal(false);

  toggle(): void {
    this.visible.update((v) => !v);
  }
}

/**
 * Wraps a password input with the usual "eye" button that shows or hides what
 * was typed. The input stays in the parent's template (so its form control
 * binding is untouched) and needs `appPasswordInput` plus right padding
 * (`pr-11`) so text never runs under the button.
 */
@Component({
  selector: 'app-password-field',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="relative">
      <ng-content />
      <button
        type="button"
        (click)="input()?.toggle()"
        [attr.aria-label]="input()?.visible() ? 'Hide password' : 'Show password'"
        [attr.aria-pressed]="input()?.visible() ? 'true' : 'false'"
        class="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-dusk transition-colors hover:text-parchment focus-visible:text-parchment focus-visible:outline-none"
      >
        @if (input()?.visible()) {
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-5 w-5"
            aria-hidden="true"
          >
            <path d="M3 3l18 18" />
            <path d="M10.6 6.1A9.6 9.6 0 0 1 12 6c5 0 8.5 4.2 9.5 6a11.6 11.6 0 0 1-2.3 2.9" />
            <path d="M6.5 7.3C4.4 8.7 3 10.7 2.5 12c1 1.8 4.5 6 9.5 6a9.7 9.7 0 0 0 3.5-.7" />
            <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
          </svg>
        } @else {
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-5 w-5"
            aria-hidden="true"
          >
            <path d="M2.5 12C3.5 10.2 7 6 12 6s8.5 4.2 9.5 6c-1 1.8-4.5 6-9.5 6s-8.5-4.2-9.5-6Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        }
      </button>
    </div>
  `,
})
export class PasswordField {
  protected readonly input = contentChild(PasswordInput);
}
